import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/material/styles';
import { IconButton, InputAdornment, TextField, Checkbox } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { ContestPrize } from '@beable/roster-backend-client-v1';
import ConfirmationDialog from './ConfirmationDialog';
import { getStyles } from './styles';

interface ContestPrizeTableProps {
    contestPrizes: ContestPrize[];
    onEditContestPrize:  ((contestPrize: ContestPrize) => void);
    onDeleteContestPrize:  ((contestPrize: ContestPrize) => void);
}

export default function ContestPrizeTable({ contestPrizes, onEditContestPrize, onDeleteContestPrize }: ContestPrizeTableProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [ contestPrizeToDelete, setContestPrizeToDelete ] = useState<ContestPrize | null>(null);
  const isDeleteConfirmDialogOpen = contestPrizeToDelete !== null;
  
  const onDeleteConfirm = () => {
    if (contestPrizeToDelete) {
      onDeleteContestPrize(contestPrizeToDelete);
    }
    setContestPrizeToDelete(null);
  };

  const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text);
  };

  return <>
  <TableContainer sx={styles.container}>
    <Table sx={styles.table} aria-label="contest prize table">
      <TableHead>
        <TableRow>
          <TableCell/>
          <TableCell/>
          <TableCell sx={styles.tableCell}>ID</TableCell>
          <TableCell sx={styles.tableCell}>Title</TableCell>
          <TableCell align="center" sx={styles.tableCell}>Gift Card Monetary Value</TableCell>
          <TableCell align="center" sx={styles.tableCell}>Points and Beable Bucks</TableCell>
          <TableCell align="center" sx={styles.tableCell}>Custom Prize</TableCell>
          <TableCell align="center" sx={styles.tableCell}>Display Name - English</TableCell>
          <TableCell align="center" sx={styles.tableCell}>Display Name - Spanish</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {contestPrizes.map((contestPrize) => (
          <TableRow key={contestPrize.id}>
            <TableCell padding='checkbox'>
              <IconButton
                aria-label="edit"
                data-testid="edit-btn"
                onClick={() => onEditContestPrize(contestPrize)}
                size="large">
                <EditIcon fontSize="small"/>
              </IconButton>
            </TableCell>
            <TableCell padding='checkbox'>
              <IconButton
                aria-label="delete"
                data-testid="delete-btn"
                onClick={() => setContestPrizeToDelete(contestPrize)}
                size="large">
                <DeleteIcon fontSize="small"/>
              </IconButton>
            </TableCell>
            <TableCell component="th" scope="row" sx={styles.tableCell}>
              <TextField 
                value={contestPrize.id}
                variant="outlined"
                fullWidth={true}
                size='small'
                slotProps={{
                  input: {
                  readOnly: true,
                  endAdornment: 
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="copy contest prize ID to clipboard"
                        edge="end"
                        onClick={ () => { copyToClipboard(contestPrize.id)}}
                        size="large">
                       <AssignmentIcon />
                      </IconButton>
                    </InputAdornment>
                  
                  }
                }}
              />  
            </TableCell>
            <TableCell component="th" scope="row" sx={styles.tableCell}>{contestPrize.title}</TableCell>
            <TableCell align="center" sx={styles.tableCell}>${(contestPrize.monetaryValue || 0).toFixed(2)}</TableCell>
            <TableCell align="center" sx={styles.tableCell}>{contestPrize.points}</TableCell>
            <TableCell align="center" padding='checkbox'>
              <Checkbox
                checked={!!contestPrize.isCustom}
                color='primary'
              />
            </TableCell>
            <TableCell align="center" sx={styles.tableCell}>{contestPrize.displayNameEn}</TableCell>
            <TableCell align="center" sx={styles.tableCell}>{contestPrize.displayNameEs}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
  <ConfirmationDialog 
              title='Confirm'
              text={`Do you want to delete "${contestPrizeToDelete?.title}"?`}
              isOpen={isDeleteConfirmDialogOpen} 
              onAccept={onDeleteConfirm} 
              onCancel={ () => { setContestPrizeToDelete(null); } }
    />
  </>;
}
